import { AxiosResponse } from 'axios';
import { IContract } from 'shared/models/contract';
import { Periodicity } from 'shared/types/periodicity';
import { apiV2 } from './http';
import {
  AddServicesRequestData,
  CreateContractRequestData,
  ListContractsRequestData,
} from './interfaces/contract';

export function listContractsByParams(
  config?: ListContractsRequestData,
): Promise<
  AxiosResponse<{
    results: IContract[];
    total: number;
  }>
> {
  return apiV2.get<{ results: IContract[]; total: number }>('contracts', {
    params: config,
  });
}

export function getContractById(
  id: number,
  includeDeleted?: boolean,
): Promise<AxiosResponse<IContract>> {
  return apiV2.get<IContract>(
    `contracts/${id}?includeDeleted=${includeDeleted}`,
  );
}

export function createContract(
  payload: CreateContractRequestData,
): Promise<AxiosResponse> {
  return apiV2.post('contracts', payload);
}

export function reactivateContract(contractId: number): Promise<AxiosResponse> {
  return apiV2.get(`contracts/reactivate/${contractId}`);
}

export function disableContract(
  contractId: number,
  should_cancel_all_open_orders: boolean,
): Promise<AxiosResponse> {
  return apiV2.post(`contracts/disable/${contractId}`, {
    should_cancel_all_open_orders,
  });
}

export function removeContract(contractId: number): Promise<AxiosResponse> {
  return apiV2.delete(`contracts/${contractId}`);
}

export function changeContractPaymentMethod(
  contractId: number,
  bankId: 1 | 2,
): Promise<AxiosResponse> {
  return apiV2.put(`contracts/${contractId}/bank`, { bankId });
}

export function changeManyContractPaymentMethod(
  contractIds: number[],
  bankId: 1 | 2,
): Promise<AxiosResponse> {
  return apiV2.post(`contracts/many/bank`, {
    contractIds,
    bankId,
  });
}

export function addContractServices(
  contractId: number,
  payload: AddServicesRequestData,
): Promise<AxiosResponse> {
  return apiV2.post(`contracts/${contractId}/services`, payload);
}

export function removeContractService(
  contractId: number,
  productId: number,
): Promise<AxiosResponse> {
  return apiV2.delete(`contracts/${contractId}/services/${productId}`);
}

export function changeContractYearlyPlan(
  contractId: number,
  contractServiceId: number,
  newServiceId: number,
  periodicity: Periodicity,
): Promise<AxiosResponse> {
  return apiV2.post(`contracts/change-plan/yearly`, {
    contract_id: contractId,
    contract_service_id: contractServiceId,
    new_service_id: newServiceId,
    periodicity: periodicity,
  });
}

export function changeContractDueDate(contractId: number, newDueDate: number) {
  return apiV2.put(`contracts/${contractId}/due-date`, {
    newDueDate,
  });
}

export function changeManyContractDueDate(
  contractIds: number[],
  newDueDate: number,
) {
  return apiV2.post(`contracts/many/due-date`, {
    contractIds: contractIds,
    newDueDate: newDueDate,
  });
}

export function changeDueDate(
  contractId: number,
  new_due_date: 5 | 10 | 15 | 20,
): Promise<AxiosResponse> {
  return apiV2.put(`contracts/update/${contractId}/due-date`, { new_due_date });
}

export interface UpdateContractServicePricePayload {
  price: number;
  discountCycles?: number;
  discountType?: string;
  discountValue?: number;
}

export function updateContractServicePrice(
  contractServiceId: number,
  payload: UpdateContractServicePricePayload,
): Promise<AxiosResponse> {
  return apiV2.put(`contracts/contract-service/${contractServiceId}`, payload);
}

export function changeExpirationDate(
  contractId: number,
  expiration_date: string,
): Promise<AxiosResponse> {
  return apiV2.post(`contracts/expiration-date/edit/${contractId}`, {
    expiration_date,
  });
}
